import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LIcon from "@components/LIcon";
import LText from "@components/LText";
import { theme } from "@theme";
import { ActivityIndicator } from "react-native";

import { Filter } from "./types";

/**
 * What the library list shows while it has no rows. `waiting` shows nothing:
 * the data is on its way and there is nothing to say yet.
 */
export type LibraryListState =
  | "waiting"
  | "empty"
  | "loading"
  | "offline"
  | "error";

/**
 * Downloads are read from the device, so their tab never waits for the
 * online library. The other tabs are views of the online library: with a
 * saved or fetched copy they behave as they always have, and without one
 * they say why the list is empty.
 *
 * The online library is a durable query that keeps fetching while offline
 * (it falls back to its saved copy), so offline is read from the device's
 * connection, not from the query.
 */
export function libraryListState({
  filter,
  offlineLibraryRead,
  hasLibrary,
  isOnline,
  isFetching,
  isError,
}: {
  filter: Filter | null;
  offlineLibraryRead: boolean;
  hasLibrary: boolean;
  isOnline: boolean;
  isFetching: boolean;
  isError: boolean;
}): LibraryListState {
  if (filter === "downloads") return offlineLibraryRead ? "empty" : "waiting";
  if (hasLibrary) return isFetching || isError ? "waiting" : "empty";
  if (!isOnline) return "offline";
  if (isError && !isFetching) return "error";
  return "loading";
}

const emptyStateText = (filter: Filter | null) => {
  switch (filter) {
    case "playlist":
      return "No playlists added,\ntap the button below to create a playlist";
    case "album":
      return "No Albums,\nAlbums you've liked will appear here";
    case "artist":
      return "No Artists,\nArtists you've liked will appear here";
    case "media":
      return "No Media,\nMedia you've liked will appear here";
    case "downloads":
      return "No downloads yet,\nYour downloads will appear here";
    case null:
      return "Albums or playlists you have added will appear here. Tap the button below to create a playlist";
  }
};

const Message = ({
  icon,
  text,
  action,
}: {
  icon?: "offline";
  text: string;
  action?: { label: string; sentryEventName: string; onPress: () => void };
}) => (
  <LBox
    alignItems="center"
    justifyContent="center"
    mt="m1"
    style={{ paddingTop: 180, paddingHorizontal: 27 }}
  >
    {icon ? <LIcon name={icon} size={42} mb="s3" /> : null}
    <LText textAlign="center" color="subText">
      {text}
    </LText>
    {action ? (
      <LButton
        accessibilityRole="button"
        label={action.label}
        sentryEventName={action.sentryEventName}
        onPress={action.onPress}
        variant="tertiary-small"
        mt="s4"
      />
    ) : null}
  </LBox>
);

const LibraryEmptyState = ({
  state,
  filter,
  onShowDownloads,
  onRetry,
}: {
  state: LibraryListState;
  filter: Filter | null;
  onShowDownloads: () => void;
  onRetry: () => void;
}) => {
  switch (state) {
    case "waiting":
      return null;
    case "empty":
      return <Message text={emptyStateText(filter)} />;
    case "loading":
      return (
        <LBox alignItems="center" mt="m1" style={{ paddingTop: 180 }}>
          <ActivityIndicator
            accessible
            accessibilityLabel="Loading your library"
            accessibilityRole="progressbar"
            color={theme.colors.white}
          />
        </LBox>
      );
    case "offline":
      return (
        <Message
          icon="offline"
          text={"You're offline,\nyour library will load when you reconnect"}
          action={{
            label: "Go to Downloads",
            sentryEventName: "library_offline_go_to_downloads",
            onPress: onShowDownloads,
          }}
        />
      );
    case "error":
      return (
        <Message
          text={
            "Couldn't load your library,\ncheck your connection and try again"
          }
          action={{
            label: "Retry",
            sentryEventName: "library_error_retry",
            onPress: onRetry,
          }}
        />
      );
  }
};

export default LibraryEmptyState;
